
import { Context } from '@nuxt/types'
import { Component, Vue } from 'nuxt-property-decorator'
import { declOfNum } from '@rshb/rshb-vue-kit/src/utils/index'
import BaseRabotaAgroProgramsSearchBanner from '@rshb/rshb-vue-kit/src/projects/rabotaAgro/banners/BaseRabotaAgroProgramsSearchBanner.vue'
import AppLazyImage from '~/components/UI/AppLazyImage.vue'
import BookCardsList from '~/components/Cards/BookCards/BookCardsList.vue'
import ArticlesSlider from '~/components/Articles/ArticlesSlider.vue'
import FutureProfessionsGrid from '~/components/Cards/FutureProfessionsCards/FutureProfessionsGrid.vue'
import UniversityCardsList from '~/components/Cards/UniversityCards/UniversityCardsList.vue'
import ProfessionInfoCarousel from '~/components/Carousels/ProfessionInfoCarousel.vue'
import InfoBlock from '~/components/InfoBlocks/InfoBlock.vue'
import EmployerCardsList from '~/components/Cards/EmployerCards/EmployerCardsList.vue'
import { EmployerModel } from '~/model/Employer'
import BreadCrumbs from '~/components/UI/BreadCrumbs.vue'
import { ProfessionModel, ProfessionTextBlockModel } from '~/model'
import { NewsCardModel } from '~/model/News'

type Universities = {
  id?: string
  price?: string
  image?: string
  programs?: string
  name?: string
  streams?: string
}

@Component({
  components: {
    FutureProfessionsGrid,
    BaseRabotaAgroProgramsSearchBanner,
    ArticlesSlider,
    ProfessionInfoCarousel,
    BookCardsList,
    InfoBlock,
    UniversityCardsList,
    EmployerCardsList,
    BreadCrumbs,
    AppLazyImage
  }
})
export default class MainPage extends Vue {
  profession: any

  async asyncData({ $services, params, error }: Context) {
    let data: any = null

    try {
      const response = await $services.requestRest.getItem<ProfessionModel>('professions', params.slug).catch(err => {
        // eslint-disable-next-line no-console
        console.error('[asyncData] Ошибка получения профессии:', err.message)
        return null
      })

      if (!response) {
        return error({
          statusCode: 404,
          message: 'Профессия не найдена'
        })
      }

      data = response
      const universities: Universities[] = []
      const colleges: Universities[] = []
      data.universities.forEach(item => {
        if (item.type === 2 && item.programs.length >= 1) {
          universities.push({
            ...item,
            id: item.id + '',
            price:
              parseInt(item.programs[0].min_price_rf) === 0
                ? 'Есть бюджетные места'
                : `От ${parseInt(item.programs[0].min_price_rf).toFixed()} ₽ в год`,
            programs: `${item.programs[0].programs_count} ${declOfNum(item.programs[0].programs_count, [
              'программа',
              'программы',
              'программ'
            ])} по профессии`,
            image: item.logo
              ? $services.image.cropImage(item.logo, {
                  mode: 'auto',
                  width: 200,
                  height: 152
                })
              : '',
            name: item.short_name ? item.short_name : item.name
          })
        }
        if (item.type === 0) {
          colleges.push({
            ...item,
            id: item.id + '',
            price: 'Среднее профессиональное образование',
            programs: `${item.streams[0].streams_count} ${declOfNum(item.streams[0].streams_count, [
              'направление',
              'направления',
              'направлений'
            ])} обучения`,
            image: item.logo
              ? $services.image.cropImage(item.logo, {
                  mode: 'auto',
                  width: 200,
                  height: 152
                })
              : '',
            name: item.short_name ? item.short_name : item.name
          })
        }
      })

      let employers: EmployerModel[] = []
      if (data.employers) {
        employers = data.employers.map(item => new EmployerModel(item))
      }
      const profession = {
        name: data.name,
        description: data.description,
        image: $services.image.cropImage(data.image, {
          extension: 'jpg',
          width: 642,
          height: 524
        }),
        books: data.books.map(book => ({
          ...book,
          id: book.id + '',
          image: $services.image.cropImage(book.image, {
            extension: 'jpg',
            width: 200,
            height: 264
          })
        })),
        news: data.news,
        newsBlockName: data.news_block_name,
        quotes: data.profession_quotes.map(quote => ({
          ...quote,
          image: $services.image.cropImage(quote.image, {
            extension: 'jpg',
            width: 1140,
            height: 520
          })
        })),
        textBlocks: data.profession_text_blocks.map(block => ({
          ...block,
          id: block.id + '',
          image_big: $services.image.cropImage(block.image_big, {
            width: 300,
            height: 300,
            mode: 'crop'
          }),
          image_small: $services.image.cropImage(block.image_small, {
            width: 166,
            height: 166,
            mode: 'crop'
          })
        })),
        skills: data.profession_skills.map(skill => ({
          ...skill,
          id: skill.id + '',
          name: skill.name,
          backgroundColor: skill.background,
          iconPath: $services.image.cropImage(skill.logo, {
            mode: 'crop'
          })
        })),
        universities,
        colleges,
        employers
      }

      return { profession }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[asyncData] Критическая ошибка:', err)
      return error({
        statusCode: 500,
        message: 'Ошибка загрузки данных профессии'
      })
    }
  }

  get restTextBlocks(): Array<ProfessionTextBlockModel> {
    const blocks = this.profession.textBlocks
    const restTextBlocks = Array.isArray(blocks) ? blocks.slice(2) : []

    return restTextBlocks.map(block => new ProfessionTextBlockModel(block))
  }

  created() {
    this.$tstore.futureProf.setFutureProfList(this.profession.skills)
    const list = Array.isArray(this.profession.news) ? this.profession.news.map(item => new NewsCardModel(item)) : []
    this.$tstore.news.setNewsList(list)
  }

  search() {
    return false
  }

  clickBookCard(slug: string) {
    this.$router.push(`/books/${slug}`)
  }

  clickUniCard(card: any) {
    this.$router.push(`/universities/${card.slug}`)
  }

  get newsList() {
    return this.$tstore.news.newsList
  }

  clickCollegeCard(card: any) {
    this.$router.push(`/colleges/${card.slug}`)
  }
}
